import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { merchantApi, type MerchantState } from '../../api/merchantApi';
import { useWalletStore } from '../../store/walletStore';
import type { Item } from '../../types/interfaces';
import styles from './MerchantModal.module.css';

interface MerchantModalProps {
  token: string;
  /** Fechar a loja. */
  onClose: () => void;
  /** Disparado após qualquer compra/venda bem-sucedida (para o Hub atualizar chips). */
  onChanged?: () => void;
}

type Tab = 'buy' | 'sell';

function itemLabel(item: Item, t: (k: string) => string): string {
  const base = item.icon_url
    ? item.icon_url.split('/').pop()?.replace(/\?.*$/, '').replace('.png', '')
    : item.id;
  const key = `item.${base}.name`;
  const tr = t(key);
  return tr !== key ? tr : item.name;
}

export default function MerchantModal({ token, onClose, onChanged }: MerchantModalProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('buy');
  const [data, setData] = useState<MerchantState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const setBalanceStore = useWalletStore(s => s.setBalance);
  const bumpInventory = useWalletStore(s => s.bumpInventory);
  const storeBalance = useWalletStore(s => s.balance);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const st = await merchantApi.state(token);
      setData(st);
      setBalanceStore(st.balance);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, setBalanceStore]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (!flash) return;
    const id = setTimeout(() => setFlash(null), 2600);
    return () => clearTimeout(id);
  }, [flash]);

  const getQty = (id: string) => Math.max(1, qty[id] ?? 1);
  const setItemQty = (id: string, n: number, max: number) =>
    setQty(q => ({ ...q, [id]: Math.min(Math.max(1, n), Math.max(1, max)) }));

  const bagFree = data ? Math.max(0, data.capacity - data.bagCount) : 0;
  // Saldo mostrado = store (atualizado no ato da compra/venda) com fallback no fetch.
  const balance = storeBalance ?? data?.balance ?? 0;

  const doBuy = async (item: Item, unit: number) => {
    if (!data) return;
    const n = getQty(item.id);
    setBusyId(item.id);
    try {
      const r = await merchantApi.buy(token, item.id, n);
      setBalanceStore(r.balance);      // ← saldo real, no ato
      bumpInventory();                 // ← mochila mudou (itens)
      setData(d => d && { ...d, balance: r.balance, bagCount: r.bagCount });
      setFlash({ kind: 'ok', msg: `${t('merchant.bought')} ${itemLabel(item, t as any)} ×${n} · −${unit * n}` });
      onChanged?.();
      load();
    } catch (e: any) {
      setFlash({ kind: 'err', msg: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const doSell = async (item: Item, unit: number) => {
    if (!data) return;
    const n = getQty(item.id);
    setBusyId(item.id);
    try {
      const r = await merchantApi.sell(token, item.id, n);
      setBalanceStore(r.balance);      // ← saldo real, no ato
      bumpInventory();                 // ← mochila mudou (itens)
      setData(d => d && { ...d, balance: r.balance, bagCount: r.bagCount });
      setFlash({ kind: 'ok', msg: `${t('merchant.sold')} ${itemLabel(item, t as any)} ×${n} · +${r.earned ?? unit * n}`
        + (r.restocked ? ` · 📦 ${t('merchant.restocked' as any)}` : '') });
      onChanged?.();
      load();
    } catch (e: any) {
      setFlash({ kind: 'err', msg: e.message });
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return tab === 'buy'
      ? data.stock.map(s => ({
          item: s.item, price: s.buy_price,
          // Modo estoque: não deixa pedir mais do que a Mercadora tem.
          max: s.unlimited || s.stock_qty == null ? 99 : Math.min(99, s.stock_qty),
          own: undefined as number | undefined,
          stock: s.unlimited ? null : s.stock_qty,
          restocks: false,
        }))
      : data.sellable.map(s => ({
          item: s.item, price: s.sell_price, max: s.quantity, own: s.quantity,
          stock: null as number | null, restocks: s.restocks,
        }));
  }, [data, tab]);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('merchant.title')}>
        <header className={styles.header}>
          <div className={styles.titleWrap}>
            <span className={styles.portrait}>🛒</span>
            <div>
              <h2 className={styles.title}>{t('merchant.title')}</h2>
              <span className={styles.subtitle}>{t('merchant.subtitle')}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.chip} title={t('merchant.balance')}>💰 {balance.toLocaleString()} <small style={{ opacity: 0.7 }}>TF</small></span>
            <span className={styles.chip} title={t('merchant.bag')}>🎒 {data ? `${data.bagCount}/${data.capacity}` : '—'}</span>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">✕</button>
          </div>
        </header>

        <div className={styles.tabs}>
          <button className={`${styles.tab} ${tab === 'buy' ? styles.tabActive : ''}`} onClick={() => setTab('buy')}>
            {t('merchant.tabBuy')}
          </button>
          <button className={`${styles.tab} ${tab === 'sell' ? styles.tabActive : ''}`} onClick={() => setTab('sell')}>
            {t('merchant.tabSell')}
          </button>
        </div>

        {flash && (
          <div className={flash.kind === 'ok' ? styles.flashOk : styles.flashErr}>{flash.msg}</div>
        )}

        <div className={styles.body}>
          {loading && <div className={styles.center}><div className={styles.spinner} /></div>}
          {error && <div className={styles.errorBox}>{error}</div>}

          {!loading && !error && rows.length === 0 && (
            <div className={styles.empty}>
              {tab === 'buy' ? t('merchant.emptyBuy') : t('merchant.emptySell')}
            </div>
          )}

          {!loading && !error && rows.map(({ item, price, max, own, stock, restocks }) => {
            const n = getQty(item.id);
            const label = itemLabel(item, t as any);
            const canAfford = tab === 'sell' || balance >= price * n;
            const roomOk = tab === 'sell' || bagFree >= n;
            const disabled = busyId === item.id || !canAfford || !roomOk;
            return (
              <div key={item.id} className={styles.row}>
                <div className={styles.iconWrap}>
                  {item.icon_url
                    ? <img src={`${item.icon_url}?v=4`} alt={label} className={styles.icon}
                        onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
                    : <span className={styles.iconPh}>{label.charAt(0)}</span>}
                </div>
                <div className={styles.info}>
                  <span className={styles.name}>{label}</span>
                  <span className={styles.meta}>
                    {item.slot}
                    {own !== undefined && <> · {t('merchant.youHave')} {own}</>}
                  </span>
                  {tab === 'buy' && (
                    <span className={stock == null ? styles.stockUnlimited : styles.stockLimited}>
                      {stock == null
                        ? `∞ ${t('merchant.stockUnlimited' as any)}`
                        : `📦 ${t('merchant.stockLeft' as any)}: ${stock}`}
                    </span>
                  )}
                  {tab === 'sell' && restocks && (
                    <span className={styles.stockLimited}>📦 {t('merchant.restocksHint' as any)}</span>
                  )}
                </div>
                <div className={styles.priceCol}>
                  <span className={styles.price}>{tab === 'buy' ? '💰' : '🪙'} {price.toLocaleString()}</span>
                  <span className={styles.priceUnit}>{t('merchant.perUnit')}</span>
                </div>
                <div className={styles.qtyStepper}>
                  <button onClick={() => setItemQty(item.id, n - 1, max)} disabled={n <= 1}>−</button>
                  <input
                    type="number" min={1} max={max} value={n}
                    onChange={e => setItemQty(item.id, parseInt(e.target.value) || 1, max)}
                  />
                  <button onClick={() => setItemQty(item.id, n + 1, max)} disabled={n >= max}>＋</button>
                </div>
                <button
                  className={tab === 'buy' ? styles.buyBtn : styles.sellBtn}
                  disabled={disabled}
                  onClick={() => (tab === 'buy' ? doBuy(item, price) : doSell(item, price))}
                >
                  {busyId === item.id
                    ? '…'
                    : tab === 'buy'
                      ? (!canAfford ? t('merchant.noGold') : !roomOk ? t('merchant.bagFull') : `${t('merchant.buy')} ${(price * n).toLocaleString()}`)
                      : `${t('merchant.sell')} ${(price * n).toLocaleString()}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
